import React from 'react';
import { createPortal } from 'react-dom';

const OrderDetails = ({ isOpen, onClose, order }) => {
  // Add print styles
  React.useEffect(() => {
    if (isOpen) {
      const style = document.createElement('style');
      style.textContent = `
        @media print {
          body * {
            visibility: hidden;
          }
          .order-content, .order-content * {
            visibility: visible;
          }
          .order-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [isOpen]);

  if (!isOpen || !order) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    if (typeof timestamp === 'string' && !isNaN(timestamp)) {
      date = new Date(parseInt(timestamp));
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      refunded: 'bg-blue-100 text-blue-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const orderText = `
BULKY MARKETING - ORDER RECEIPT
===============================

Order ID: #${order.id}
Date: ${formatDate(order.created_at)}

PACKAGE INFORMATION:
- Package: ${order.package?.name || 'N/A'}
- Duration: ${order.package?.duration_days || 'N/A'} days

ORDER DETAILS:
- Amount: ₹${order.amount}
- Currency: ${order.currency}
- Status: ${order.status?.toUpperCase()}

PAYMENT INFORMATION:
- Razorpay Order ID: ${order.razorpay_order_id || 'N/A'}
- Payment ID: ${order.razorpay_payment_id || 'N/A'}
- Payment Method: ${order.payment_method || 'N/A'}

===============================
BULKY MARKETING
Professional Marketing Solutions
Email: support@bulkymarketing.com
Website: www.bulkymarketing.com

Thank you for your business!
    `;

    const blob = new Blob([orderText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulky-marketing-order-${order.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-container max-w-3xl">
        {/* Header */}
        <div className="modal-header no-print bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white">
              <span className="material-icons-round text-sm">sticky_note_2</span>
            </div>
            <div>
              <h2 className="text-xl font-medium text-white tracking-tight">Transaction Snapshot</h2>
              <p className="text-[10px] text-white/70 font-normal uppercase tracking-wider leading-none mt-0.5">Historical Order Audit</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
              title="Print Receipt"
            >
              <span className="material-icons-round text-sm">print</span>
            </button>
            <button
              onClick={handleDownload}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
              title="Download Data"
            >
              <span className="material-icons-round text-sm">download</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
            >
              <span className="material-icons-round text-sm">close</span>
            </button>
          </div>
        </div>

        {/* Order Content */}
        <div className="modal-content !p-0 order-content">
          <div className="p-10">
            {/* Company Header */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-white border border-gray-100 rounded-2xl flex items-center justify-center p-2 shadow-sm">
                  <img
                    src="/bm-logo.png"
                    alt="Bulky Marketing Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-bulky-navy-800 tracking-tight">Bulky Marketing</h2>
                  <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mt-0.5">Infrastructural Systems</p>
                </div>
              </div>

              <div className="md:text-right">
                <div className="bg-gray-50/50 border border-gray-100 rounded-2xl p-4 md:p-6">
                  <h2 className="text-xs font-medium text-bulky-navy-400 uppercase tracking-widest mb-3">Order Metadata</h2>
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-wider">
                      <span className="text-gray-400">ID:</span> <span className="font-normal">#{order.id.substring(0, 10)}</span>
                    </p>
                    <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-wider">
                      <span className="text-gray-400">TS:</span> {formatDate(order.created_at)}
                    </p>
                    <div className="pt-1.5">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider ${getStatusColor(order.status)} border border-current shadow-sm`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Package Information */}
              <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-2xl p-6 text-white group overflow-hidden relative">
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/5 rounded-full -mb-12 -mr-12 blur-xl"></div>
                <h2 className="text-xs font-medium text-white/40 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <span className="material-icons-round text-xs">inventory_2</span>
                  Node Specification
                </h2>
                <div className="space-y-4">
                  <div>
                    <span className="text-[9px] font-normal text-white/30 uppercase tracking-wider block mb-0.5">Asset Tier:</span>
                    <span className="text-base font-normal uppercase tracking-tight">{order.package?.name || 'GENERIC NODE'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-[9px] font-normal text-white/30 uppercase tracking-wider block mb-0.5">Cycle Length:</span>
                      <span className="text-xs font-normal uppercase tracking-tight">{order.package?.duration_days || '0'} Days</span>
                    </div>
                    <div>
                      <span className="text-[9px] font-normal text-white/30 uppercase tracking-wider block mb-0.5">Unit Price:</span>
                      <span className="text-xs font-normal uppercase tracking-tight">₹{order.package?.price || '0.00'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/5 rounded-full -mt-8 -mr-8 blur-lg"></div>
                <h2 className="text-xs font-medium text-emerald-600/60 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <span className="material-icons-round text-xs text-emerald-500">receipt</span>
                  Valuation Summary
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-emerald-100 pb-2">
                    <span className="text-[9px] font-normal text-emerald-600/40 uppercase tracking-wider">Base Amount:</span>
                    <span className="text-xs font-normal text-emerald-800 uppercase tracking-tight">₹{order.amount}</span>
                  </div>
                  <div className="flex justify-between border-b border-emerald-100 pb-2">
                    <span className="text-[9px] font-normal text-emerald-600/40 uppercase tracking-wider">Currency:</span>
                    <span className="text-xs font-normal text-emerald-800 uppercase tracking-tight">{order.currency}</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="text-[10px] font-normal text-emerald-800 uppercase tracking-wider">Total Settled:</span>
                    <span className="text-base font-normal text-emerald-600 uppercase tracking-tighter">₹{order.amount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Hardware Information */}
            <div className="bg-gray-50/80 border border-gray-100 rounded-2xl p-8 mb-10">
              <h2 className="text-xs font-medium text-bulky-navy-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                <span className="material-icons-round text-xs">payment</span>
                Gateway Handshake Data
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <div>
                  <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider block mb-1.5">Order Fragment Identification:</span>
                  <div className="text-[9px] font-normal text-bulky-navy-800 font-mono bg-white border border-gray-100 p-2 rounded-lg break-all uppercase tracking-wider">
                    {order.razorpay_order_id || 'LOCAL-NODE-00'}
                  </div>
                </div>
                <div>
                  <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider block mb-1.5">Settlement fragment:</span>
                  <div className="text-[9px] font-normal text-bulky-navy-800 font-mono bg-white border border-gray-100 p-2 rounded-lg break-all uppercase tracking-wider">
                    {order.razorpay_payment_id || 'LOCAL-EXEC-00'}
                  </div>
                </div>
                <div className="flex justify-between items-center border-t border-gray-100 pt-4 md:col-span-2">
                  <div className="flex flex-col">
                    <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Authorization Method:</span>
                    <span className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-wider">{order.payment_method || 'GATEWAY_DEFAULT'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Final Confirmation:</span>
                    <span className={`text-[10px] font-normal uppercase tracking-wider px-3 py-1 rounded-lg ${getStatusColor(order.status)} border border-current`}>
                      {order.status || 'VERIFIED'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-50 pt-10 text-center flex flex-col items-center">
              <div className="w-10 h-0.5 bg-gray-200 rounded-full mb-6"></div>
              <p className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-[0.25em] mb-2">Technical Receipt Finalized</p>
              <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider max-w-xs leading-relaxed opacity-60">
                This document serves as primary proof of transmission for system resource allocation.
              </p>
              <div className="mt-8">
                <p className="text-[8px] font-normal text-gray-200 uppercase tracking-[0.4em]">FRAGMENT_HASH_{order.id.toUpperCase()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrderDetails;